import { useEffect, useState } from "react";
import { callLlm } from "../lib/forecast/llmClient.js";

export const CACHE_MAX_ENTRIES = 100;
// Requests in flight, by cache key: reopening the brief before an answer arrives joins the
// request already made rather than paying for a second.
const inflight = new Map();

/** A short, stable storage key for (long) prompt inputs: FNV-1a over the JSON. */
function hashKey(text) {
  let h = 0x811c9dc5;
  for (let i = 0; i < text.length; i++) {
    h ^= text.charCodeAt(i);
    h = Math.imul(h, 0x01000193);
  }
  return `${(h >>> 0).toString(36)}-${text.length.toString(36)}`;
}

// Stored for a question the model answered unusably, so the brief shows its own text at once
// instead of asking again on every open.
const NONE = "∅";
// Asked this session and failed (the model could not be reached): settled until a reload.
const failed = new Set();

/**
 * What is known about `key`: `undefined` when it has not been asked, `null` when it was and no
 * usable answer came back, else the answer.
 */
function lookup(name, key, clean) {
  if (failed.has(`${name}:${key}`)) return null;
  try {
    const raw = globalThis.localStorage?.getItem(`sooth-${name}:${key}`);
    if (raw == null) return undefined;
    if (raw === NONE) return null;
    return clean(raw) ?? undefined;
  } catch {
    return undefined;
  }
}

/**
 * Cache `value` (as JSON) for `name` under `key`, evicting that name's oldest entries beyond
 * CACHE_MAX_ENTRIES.
 */
export function writeCache(name, key, value) {
  try {
    const ls = globalThis.localStorage;
    if (!ls) return;
    const indexKey = `sooth-${name}Index`;
    let index;
    try {
      index = JSON.parse(ls.getItem(indexKey) || "[]");
    } catch {
      index = [];
    }
    index = (Array.isArray(index) ? index : []).filter((k) => k !== key);
    index.push(key);
    while (index.length > CACHE_MAX_ENTRIES) ls.removeItem(`sooth-${name}:${index.shift()}`);
    ls.setItem(indexKey, JSON.stringify(index));
    ls.setItem(`sooth-${name}:${key}`, value === NONE ? NONE : JSON.stringify(value));
  } catch {
    // Storage full or blocked: the answer is simply asked for again next time.
  }
}

/**
 * One small-LLM answer for the brief (no web search), written once per set of `inputs` and
 * cached in this browser under `name`. `clean` turns the model's text into the value, or null
 * when it is unusable; it also re-reads the cached JSON, so it must accept its own output.
 * Null until the answer arrives, when it fails, or while `enabled` is false.
 *
 * @param {{ enabled: boolean, name: string, version: number, inputs: unknown,
 *   prompt: () => string, clean: (text: string) => unknown }} deps
 * @returns {{ value: unknown, pending: boolean }}
 */
export function useBriefLlm({ enabled, name, version, inputs, prompt, clean }) {
  const key = enabled ? llmKeyOf(version, inputs) : null;
  const known = key ? lookup(name, key, clean) : undefined;
  const [fetched, setFetched] = useState({});
  useEffect(() => {
    if (!key || known !== undefined || key in fetched) return undefined;
    let live = true;
    requestAnswer({ name, key, prompt, clean }).then((value) => {
      if (live) setFetched((f) => ({ ...f, [key]: value }));
    });
    return () => {
      live = false;
    };
    // `fetched` is read only to skip a key already asked for; re-running on it would re-ask.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key, known !== undefined]);
  const value = known ?? (key ? fetched[key] : null) ?? null;
  return { value, pending: !!key && known === undefined && !(key in fetched) };
}

// The version is part of every key: bump it when the prompt changes, so answers are redone.
const llmKeyOf = (version, inputs) => hashKey(JSON.stringify([version, inputs]));

/** The answer for `key`, asked for once: a second caller joins the request in flight. */
function requestAnswer({ name, key, prompt, clean }) {
  const slot = `${name}:${key}`;
  if (!inflight.has(slot))
    inflight.set(
      slot,
      callLlm(prompt(), { webSearch: false })
        .then(
          (text) => {
            const value = clean(text) ?? null;
            writeCache(name, key, value ?? NONE);
            return value;
          },
          () => {
            failed.add(slot);
            return null;
          },
        )
        .finally(() => inflight.delete(slot)),
    );
  return inflight.get(slot);
}

/**
 * Starts the answer a `useBriefLlm` call with the same spec would ask for, before anything
 * renders it, so it is cached (or on its way) when the brief opens. No-op when already cached.
 * @param {{ name: string, version: number, inputs: unknown, prompt: () => string,
 *   clean: (text: string) => unknown }} spec
 */
export function prefetchBriefLlm({ name, version, inputs, prompt, clean }) {
  const key = llmKeyOf(version, inputs);
  if (lookup(name, key, clean) === undefined) requestAnswer({ name, key, prompt, clean });
}
